import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  LogOut, 
  Clock,
  CheckCircle, 
  XCircle,
  Eye,
  TrendingUp,
  Calendar,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mapování festivalů
const FESTIVAL_NAMES = {
  'kontakt-fest-2026': 'KONTAKT Fest 2026',
  'cesta-kolem-sveta-2026': 'Gastrofestival Cesta Kolem Světa',
  'otrokovicke-letni-slavnosti-2026': 'Otrokovické letní slavnosti',
  'festival-koupacek-2026': 'Festival Koupáček',
  'burger-rock-fest-2026': 'Burger Rock Fest Napajedla',
  'majofest-otrokovice-2026': 'Májofest Otrokovice',
  'den-zk-2026': 'Den Zlínského kraje'
};

const AdminOverview = () => {
  const [allApplications, setAllApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    byFestival: {}
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadAllApplications();
  }, []);

  const loadAllApplications = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Získej všechny přihlášky (bez event_id filtru)
      const response = await axios.get(`${BACKEND_URL}/api/vendor-applications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const apps = response.data;
      setAllApplications(apps);
      
      // Vypočítej statistiky
      const statsData = {
        total: apps.length,
        pending: apps.filter(a => a.status === 'pending').length,
        approved: apps.filter(a => a.status === 'approved').length,
        rejected: apps.filter(a => a.status === 'rejected').length,
        byFestival: {}
      };

      // Inicializuj všech 7 festivalů (aby se zobrazily i bez přihlášek)
      const allFestivalIds = [
        'kontakt-fest-2026',
        'cesta-kolem-sveta-2026',
        'otrokovicke-letni-slavnosti-2026',
        'festival-koupacek-2026',
        'burger-rock-fest-2026',
        'majofest-otrokovice-2026',
        'den-zk-2026'
      ];

      allFestivalIds.forEach(eventId => {
        statsData.byFestival[eventId] = {
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0
        };
      });

      // Statistiky podle festivalů
      apps.forEach(app => {
        const eventId = app.eventId || 'unknown';
        if (!statsData.byFestival[eventId]) {
          statsData.byFestival[eventId] = {
            total: 0,
            pending: 0,
            approved: 0,
            rejected: 0
          };
        }
        statsData.byFestival[eventId].total++;
        statsData.byFestival[eventId][app.status]++;
      });

      setStats(statsData);
      
      // Debug: Vypiš všechny unikátní eventId v konzoli
      const uniqueEventIds = [...new Set(apps.map(app => app.eventId))];
      console.log('🔍 Všechny eventId v databázi:', uniqueEventIds);
      console.log('📊 Statistiky podle eventId:', statsData.byFestival);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        toast.error('Session vypršela, přihlaste se znovu');
        navigate('/admin/login');
      } else {
        toast.error('Chyba při načítání přihlášek');
      }
      console.error('Error loading applications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Odhlášení úspěšné');
    navigate('/admin/login');
  };

  const goToFestivalDashboard = (eventId) => {
    console.log('🔍 Navigace na festival:', eventId);
    navigate(`/admin/dashboard/${eventId}`);
  };

  const goToEventSelector = () => {
    navigate('/admin/events');
  };

  // Zobraz posledních 10 nových přihlášek
  const recentApplications = allApplications
    .filter(app => app.status === 'pending')
    .slice(0, 10);

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Čeká na schválení', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { label: 'Schváleno', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { label: 'Zamítnuto', color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const cfg = config[status] || config.pending;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${cfg.color}`}>
        <Icon className="w-4 h-4" />
        {cfg.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítám přehled...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Přehled všech festivalů</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={goToEventSelector}
              variant="outline"
              className="border-orange-300 hover:bg-orange-50"
            >
              Vybrat festival
            </Button>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-red-300 hover:bg-red-50 text-red-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Odhlásit
            </Button>
          </div>
        </div>

        {/* Statistiky - celkové */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Celkem přihlášek</p>
                  <p className="text-4xl font-black text-blue-600">{stats.total}</p>
                </div>
                <TrendingUp className="w-12 h-12 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Čeká na schválení</p>
                  <p className="text-4xl font-black text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="w-12 h-12 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Schváleno</p>
                  <p className="text-4xl font-black text-green-600">{stats.approved}</p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Zamítnuto</p>
                  <p className="text-4xl font-black text-red-600">{stats.rejected}</p>
                </div>
                <XCircle className="w-12 h-12 text-red-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Statistiky podle festivalů */}
        <Card className="mb-8 border-2 border-orange-200">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Přihlášky podle festivalů
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Zobrazit festivaly ve správném pořadí */}
              {[
                'kontakt-fest-2026',
                'cesta-kolem-sveta-2026',
                'otrokovicke-letni-slavnosti-2026',
                'festival-koupacek-2026',
                'burger-rock-fest-2026',
                'majofest-otrokovice-2026',
                'den-zk-2026'
              ].map(eventId => {
                const festStats = stats.byFestival[eventId] || { total: 0, pending: 0, approved: 0, rejected: 0 };
                return (
                  <div 
                    key={eventId}
                    onClick={() => goToFestivalDashboard(eventId)}
                    className="bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-orange-400 hover:shadow-lg transition-all cursor-pointer"
                  >
                    <h3 className="font-bold text-lg mb-3 text-slate-900">
                      {FESTIVAL_NAMES[eventId] || eventId}
                    </h3>
                    {/* Debug info - zobraz eventId */}
                    <p className="text-xs text-gray-500 mb-2 font-mono">ID: {eventId}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-blue-50 p-2 rounded">
                        <p className="text-gray-600 text-xs">Celkem</p>
                        <p className="font-bold text-blue-600">{festStats.total}</p>
                      </div>
                      <div className="bg-yellow-50 p-2 rounded">
                        <p className="text-gray-600 text-xs">Čeká</p>
                        <p className="font-bold text-yellow-600">{festStats.pending}</p>
                      </div>
                      <div className="bg-green-50 p-2 rounded">
                        <p className="text-gray-600 text-xs">Schváleno</p>
                        <p className="font-bold text-green-600">{festStats.approved}</p>
                      </div>
                      <div className="bg-red-50 p-2 rounded">
                        <p className="text-gray-600 text-xs">Zamítnuto</p>
                        <p className="font-bold text-red-600">{festStats.rejected}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Debug sekce - Neznámé eventId */}
        {Object.keys(stats.byFestival).filter(id => !['kontakt-fest-2026', 'cesta-kolem-sveta-2026', 'otrokovicke-letni-slavnosti-2026', 'festival-koupacek-2026', 'burger-rock-fest-2026', 'majofest-otrokovice-2026'].includes(id)).length > 0 && (
          <Card className="border-2 border-red-300 bg-red-50">
            <CardHeader>
              <CardTitle className="text-xl text-red-700">⚠️ Neznámé eventId v databázi</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 mb-4">Tyto přihlášky mají eventId, které není v seznamu 6 festivalů:</p>
              {Object.entries(stats.byFestival)
                .filter(([id]) => !['kontakt-fest-2026', 'cesta-kolem-sveta-2026', 'otrokovicke-letni-slavnosti-2026', 'festival-koupacek-2026', 'burger-rock-fest-2026', 'majofest-otrokovice-2026'].includes(id))
                .map(([eventId, festStats]) => (
                  <div key={eventId} className="bg-white p-3 rounded mb-2 border border-red-300">
                    <p className="font-mono text-sm font-bold text-red-700">eventId: "{eventId}"</p>
                    <p className="text-sm text-gray-600">Přihlášek: {festStats.total} (pending: {festStats.pending}, approved: {festStats.approved}, rejected: {festStats.rejected})</p>
                  </div>
                ))}
            </CardContent>
          </Card>
        )}

        {/* Poslední nové přihlášky */}
        <Card className="border-2 border-orange-200">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Bell className="w-6 h-6" />
              Nové přihlášky čekající na schválení ({stats.pending})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {recentApplications.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">Žádné nové přihlášky</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentApplications.map((app) => (
                  <div 
                    key={app.id}
                    className="bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-orange-400 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg text-slate-900">{app.vendorName}</h3>
                          {getStatusBadge(app.status)}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-semibold">Festival:</span> {FESTIVAL_NAMES[app.eventId] || app.eventId}
                        </p>
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-semibold">Kontakt:</span> {app.name} • {app.email} • {app.phone}
                        </p>
                        <p className="text-xs text-gray-500">
                          Podáno: {new Date(app.createdAt).toLocaleDateString('cs-CZ', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <Button
                        onClick={() => goToFestivalDashboard(app.eventId)}
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Zobrazit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOverview;
